import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchDashboard } from "../services/dashboardService";
import { EMPTY_DASHBOARD } from "../types/dashboard.types";
import type { DashboardSummary } from "../types/dashboard.types";

export type DashboardStatus = "loading" | "ready" | "failed";

export interface DashboardState {
  /**
   * The figures, and `EMPTY_DASHBOARD` until they arrive or when they did not.
   * Never null, so the cards render one shape throughout - a summary whose
   * counts are `null` is what "no figure yet" already looks like.
   */
  summary: DashboardSummary;
  status: DashboardStatus;
  /** What the failed state's retry calls. */
  reload: () => void;
}

export const DASHBOARD_QUERY_KEY = ["dashboard", "summary"] as const;

/**
 * The dashboard's figures, loaded on mount.
 *
 * One call for the whole screen - see `fetchDashboard`. Backed by TanStack
 * Query, so a screen visited again shows last time's figures immediately
 * while a fresh read runs quietly behind it — a client added on another
 * screen still catches up to the counts within moments rather than forcing a
 * spinner on every visit.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useDashboard(): DashboardState {
  const getToken = useTokenGetter();
  const toast = useToast();

  const query = useQuery({
    queryKey: DASHBOARD_QUERY_KEY,
    queryFn: ({ signal }) => fetchDashboard(getToken, signal),
  });

  // TanStack Query only flips `isError` when there is no cached data to fall
  // back on, so a background refresh failing behind figures already on
  // screen never reaches this — the dietitian keeps seeing the last real
  // answer instead of a toast about a screen that still works.
  useEffect(() => {
    if (!query.isError) return;
    console.error("dashboard failed to load:", query.error);
    toast.error(
      "Could not load your dashboard",
      query.error instanceof ApiError
        ? query.error.message
        : "We could not reach the server. Check your connection and try again."
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError, query.error]);

  return {
    summary: query.data ?? EMPTY_DASHBOARD,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    reload: () => {
      void query.refetch();
    },
  };
}
